import { ChangeEvent } from 'react';
import { Card } from '@nimbus-ds/components';
import { Stack } from 'commons/components';
import { SelectedZonesInterface } from 'domains/Shipping/components/shared/ZoneManagement/DeliveryZoneCard/DeliveryZoneCard';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import DeliveryZonesSection from './components/DeliveryZonesSection';
import DistributionCenterSection from './components/DistributionCenterSection';
import Skeleton from './Skeleton';
import {
  UpsellProtectionProvider,
  type UpsellProtectionConfig,
} from '../../../hooks';

interface ShippingDisponibilityCardProps {
  distributionCenter?: string;
  deliveryZones?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  specificDistributionCenters?: string[];
  selectedZones?: SelectedZonesInterface[];
  distanceInKm?: string;
  onDistributionCentersChange: (centers: string[]) => void;
  onDeliveryZonesChange: (zones: SelectedZonesInterface[]) => void;
  errors?: Partial<{ distanceInKm: string }>;
  upsellConfig?: UpsellProtectionConfig;
}

interface ShippingDisponibilityCardContentProps {
  distributionCenter?: string;
  deliveryZones?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  specificDistributionCenters: string[];
  selectedZones: SelectedZonesInterface[];
  distanceInKm: string;
  onDistributionCentersChange: (centers: string[]) => void;
  onDeliveryZonesChange: (zones: SelectedZonesInterface[]) => void;
  errors: Partial<{ distanceInKm: string }>;
}

function ShippingDisponibilityCardContent({
  distributionCenter,
  deliveryZones,
  onChange,
  specificDistributionCenters,
  selectedZones,
  distanceInKm,
  onDistributionCentersChange,
  onDeliveryZonesChange,
  errors,
}: Readonly<ShippingDisponibilityCardContentProps>): JSX.Element {
  const t = useTranslationShipping(
    'deliveryMethods.shippingLimiting.availability',
  );

  return (
    <Card>
      <Card.Header title={t('title')} />
      <Card.Body>
        <Stack column align="stretch">
          <Stack.Item>
            <DistributionCenterSection
              distributionCenter={distributionCenter}
              onChange={onChange}
              specificDistributionCenters={specificDistributionCenters}
              onDistributionCentersChange={onDistributionCentersChange}
            />
          </Stack.Item>
          <Stack.Item>
            <DeliveryZonesSection
              deliveryZones={deliveryZones}
              onChange={onChange}
              selectedZones={selectedZones}
              distanceInKm={distanceInKm}
              onDeliveryZonesChange={onDeliveryZonesChange}
              errors={errors}
            />
          </Stack.Item>
        </Stack>
      </Card.Body>
    </Card>
  );
}

function ShippingDisponibilityCard({
  distributionCenter,
  deliveryZones,
  onChange,
  specificDistributionCenters = [],
  selectedZones = [],
  distanceInKm = '',
  onDistributionCentersChange,
  onDeliveryZonesChange,
  errors = {},
  upsellConfig,
}: Readonly<ShippingDisponibilityCardProps>): JSX.Element {
  if (upsellConfig) {
    return (
      <UpsellProtectionProvider
        enabled={upsellConfig.enabled}
        featureKey={upsellConfig.featureKey}
        trackingSource={upsellConfig.trackingSource}
        title={upsellConfig.title}
      >
        <ShippingDisponibilityCardContent
          distributionCenter={distributionCenter}
          deliveryZones={deliveryZones}
          onChange={onChange}
          specificDistributionCenters={specificDistributionCenters}
          selectedZones={selectedZones}
          distanceInKm={distanceInKm}
          onDistributionCentersChange={onDistributionCentersChange}
          onDeliveryZonesChange={onDeliveryZonesChange}
          errors={errors}
        />
      </UpsellProtectionProvider>
    );
  }

  return (
    <ShippingDisponibilityCardContent
      distributionCenter={distributionCenter}
      deliveryZones={deliveryZones}
      onChange={onChange}
      specificDistributionCenters={specificDistributionCenters}
      selectedZones={selectedZones}
      distanceInKm={distanceInKm}
      onDistributionCentersChange={onDistributionCentersChange}
      onDeliveryZonesChange={onDeliveryZonesChange}
      errors={errors}
    />
  );
}

ShippingDisponibilityCard.Skeleton = Skeleton;

export default ShippingDisponibilityCard;
